import { slezhenie } from './aktivnost';
import { otmetitSvoyNabor } from './svoyNabor';
import { apiClient } from '../../api/client';

/** Статусы менеджера (ТЗ §2.6). Словарь один на систему — фильтр и строка зовут их одинаково. */
export type ManagerStatusName = 'ready' | 'break' | 'away' | 'on_call' | 'processing';

export interface AwayKind {
  id: number;
  name: string;
  max_sec: number | null;
}

export interface WorkplaceLineBrief {
  id: number;
  name: string;
  number: string | null;
  active: boolean;
}

/**
 * Состояние своего рабочего места (ТЗ §4.5, карта 2.1).
 *
 * `lock` приходит с сервера, а не считается на экране: тем же ответом сервер
 * отказывает в смене статуса. Считать замок отдельно на фронте значило бы
 * рано или поздно гасить кнопку там, где сервер пускает, — или наоборот.
 */
/** Идущий разговор — тот, что начал СЕРВЕР (обзвон или входящий). */
export interface ActiveCall {
  call_id: number;
  client_phone: string;
  direction: 'inbound' | 'outbound';
  contact_name: string | null;
  /** Откуда звонок: имя обзвона, «Входящий» или «Ручной набор» (§4.5). */
  propushchennyy?: boolean;
  obzvony?: { id: number; name: string }[];
  source: string;
  task_id: number | null;
  source_ref: Record<string, unknown> | null;
  /** `s726`: звонок из карточки (а не заход обзвона). */
  ruchnoy?: boolean;
  /** Которая попытка захода и сколько их всего у обзвона. */
  popytka?: number | null;
  popytok_vsego?: number | null;
  kommentariy?: string | null;
  /** Карточка, к которой относится звонок: лид или запись. */
  kartochka?: KartochkaZvonka | null;
  /** `s732`: линия ИМЕННО этого звонка — её записал учёт; null — учёт её ещё не знает. */
  line?: string | null;
  /** `s766`: звонок без задачи обзвона — клиентку ищет сервер по номеру. */
  neskolko?: boolean;
  zapis?: ZapisKlientki | null;
}

/** `s766`: карточка, к которой ведёт звонок: лид, запись или сама клиентка. */
export interface KartochkaZvonka { vid: 'lid' | 'zapis' | 'klientka'; id: number; url: string }

/** `s766`: ближайшая запись клиентки; время — строкой сервера, по Москве. */
export interface ZapisKlientki { id: number; kogda: string; url: string }

/** `s766`: входящий, который станция прямо сейчас соединяет с этим рабочим местом. */
export interface Vhodyashchiy {
  key: string;
  line: string | null;
  client_phone: string | null;
  direction: 'inbound';
  contact_name: string | null;
  neskolko: boolean;
  kartochka: KartochkaZvonka | null;
  zapis: ZapisKlientki | null;
  /** Сколько человек прождал на линии до соединения; null — не ждал. */
  zhdal_sek: number | null;
}

/** `s726`: идёт набор этому рабочему месту — трубку ещё не сняли. */
export interface Nabor {
  attempt_id: number;
  started_at: string | null;
  task_id: number;
  contact_name: string | null;
  client_phone: string;
  propushchennyy?: boolean;
  obzvony?: { id: number; name: string }[];
  source: string | null;
  ruchnoy: boolean;
  popytka: number;
  popytok_vsego: number | null;
  kommentariy: string | null;
  kartochka: KartochkaZvonka | null;
  /** `s732`: линия, с которой идёт этот набор. */
  line?: string | null;
}

/** `s730`: пропущенный входящий, по которому ещё не перезвонили; номер — маской. */
export interface Propushchennyy {
  obzvony?: { id: number; name: string }[];
  task_id: number;
  client_phone: string | null;
  at: string | null;
  line: string | null;
  /** `s732`: перезвон ждёт свою линию — с другого номера станция не звонит. */
  liniya_nedostupna?: boolean;
  /** `s777`: что робот уже сделал с пропущенным — сколько раз набирал и когда наберёт снова; готовой фразой сервера. */
  robot?: string | null;
}

export interface WorkplaceState {
  words?: { texts: Record<string, string> };
  manager_id: number;
  status: ManagerStatusName;
  away_kind: string | null;
  away_kind_id: number | null;
  since: string | null;
  seconds_in_status: number;
  planned_until: string | null;
  seconds_left: number | null;
  extensions: number;
  source: string | null;
  /** `s779`: срок обработки на виду — когда станция без ответа поставит «Отошёл» и на сколько минут можно продлить. */
  obrabotka?: { away_at: string | null; prodlit_minuty: number[] };
  /** `s779`: правило бездействия в «Готов» — сколько тишины панель терпит и сколько ждёт отклика (0 — не следить). */
  bezdeystvie?: { limit_sek: number; zhdat_sek: number };
  /** `s779`: «Отошёл» поставила станция — тишина в панели либо нет ответа после обработки; null — статус свой. */
  prinuditelno?: 'idle' | 'processing' | null;
  lock: { locked: boolean; reason: string | null };
  active_call: ActiveCall | null;
  /** `s726`: разговор, который дооформляется в «Обработке» (переживает перезагрузку страницы). */
  posledniy_zvonok?: ActiveCall | null;
  /** `s726`: кого сейчас набирают этому месту; null — набора нет. */
  nabor?: Nabor | null;
  /** `s766`: входящий, который станция соединяет с этим местом прямо сейчас; null — такого нет. */
  vhodyashchiy?: Vhodyashchiy | null;
  /** `s726`: действующие линии для ручного набора. */
  linii?: { id: number; name: string; number: string | null }[];
  /** `s726`: сколько задач обзвона ждут своего часа у обзвонов этого человека. */
  ochered?: number;
  /** `s730`: сколько клиентов сейчас ждут на линиях этого человека (он занят или дооформляет звонок). */
  zhdut_na_linii?: number;
  /** `s732`: перезвоны, которым пора (пропущенные и назначенные); на потом; задачи остальных обзвонов. */
  perezvonov?: number;
  perezvonov_pozzhe?: number;
  v_obzvonah?: number;
  /** `s730`: станция умеет перевод разговора; без этого кнопка «Перевод» не рисуется (перевод обрывал звонок). */
  perevod?: boolean;
  /** `s730`: пропущенные входящие без перезвона на линиях, которые принимает этот человек. */
  propushchennye?: Propushchennyy[];
  /** Подключён ли софтфон прямо сейчас — ответ ДВИЖКА, а не браузера (§5.9). */
  line_online: boolean;
  away_kinds: AwayKind[];
  break_presets_minutes: number[];
  workplace: {
    username: string;
    display_name: string;
    line: WorkplaceLineBrief | null;
  } | null;
  /** Куда уходить за клиентом (§10a) — адреса настроенных интеграций, не выдумки. */
  crm: { amocrm: string | null; yclients: string | null };
  server_time: string;
}

export async function fetchWorkplaceState(): Promise<WorkplaceState | null> {
  const { data } = await apiClient.get<WorkplaceState>('/telephony/workplace/state');

  /*
   * ⛔ s523 — ОТВЕТ ПРОВЕРЯЕТСЯ, А НЕ ПРИНИМАЕТСЯ НА ВЕРУ.
   *
   * Тип обещает, что поле lock есть всегда, и сервер его действительно кладёт
   * всегда. Но между сервером и этой строкой стоит всё остальное: страница
   * входа, отданная с кодом 200, ответ прокси, пустое тело — и, главное,
   * вкладка, открытая ЧЕРЕЗ выкат, которая старым кодом опрашивает новый ответ.
   * Любой такой случай давал объект без lock, а читали его голым: `state?.lock.locked`
   * коротит только на state. Двенадцать issue в приборе, последнее — этим утром.
   *
   * Годность материала проверяется ЗДЕСЬ, в одном месте, а не пятью
   * вопросительными знаками по экранам: не тот ответ — значит, состояния нет.
   */
  if (data === null || typeof data !== 'object' || typeof (data as WorkplaceState).lock !== 'object'
      || (data as WorkplaceState).lock === null) {
    return null;
  }

  return data;
}

/** Смена статуса руками: Готов / Перерыв / Отошёл (ТЗ §2.6). */
export async function changeStatus(payload: {
  status: 'ready' | 'break' | 'away';
  minutes?: number;
  away_kind_id?: number;
}): Promise<void> {
  await apiClient.post('/telephony/managers/status', payload);
}

/** «Продлить» обработку: на выбранные минуты (`s779`), без выбора — тем же интервалом (ТЗ §2.6). */
export async function extendProcessing(minutes?: number): Promise<void> {
  await apiClient.post('/telephony/managers/processing/extend', minutes ? { minutes } : undefined);
}

/** `s779`: в «Готов» давно нет ни движения, ни нажатий, и на вопрос экрана не ответили — станция ставит «Отошёл». */
export async function soobshitOBezdeystvii(): Promise<void> {
  // `s781`: станция пишет в чат, чего не было — действий за компьютером или только в панели.
  await apiClient.post('/telephony/managers/idle', { watch: slezhenie() });
}

/** `s781`: что браузер этого человека решил о «вижу, что вы за компьютером» — в журнал станции, для разбора жалоб. */
export async function soobshitOPrisutstvii(state: 'unsupported' | 'prompt' | 'granted' | 'denied'): Promise<void> {
  await apiClient.post('/telephony/managers/presence', { state });
}

/** «Завершить» обработку досрочно — возврат в «Готов». */
export async function finishProcessing(): Promise<void> {
  await apiClient.post('/telephony/managers/processing/finish');
}

/**
 * Отметка «телефон на линии» (§5.9).
 *
 * Браузер знает про свою регистрацию точно, а серверу спросить движок из
 * веб-процесса удаётся не всегда: без этой отметки человек с подключённым
 * телефоном не мог встать в «Готов».
 */
export async function reportRegistration(registered: boolean): Promise<void> {
  await apiClient.post('/telephony/workplace/registration', { registered });
}

/**
 * s734: соединение телефона оборвалось — браузер называет серверу, что он об этом знает.
 * Станция видит только «та сторона закрыла»; без этой строки причину обрывов не найти.
 */
export interface ObryvTelefona {
  prichina: string;
  vkladka: string;
  set: boolean;
  razgovor: boolean;
  zhilo_sek: number;
}

export async function soobshchitObObryve(obryv: ObryvTelefona): Promise<void> {
  await apiClient.post('/telephony/workplace/registration', { registered: false, obryv });
}

/** Живая лента разговора (ТЗ §2.11) — как её отдаёт сервер. */
export interface LiveTranscriptPayload {
  call_id: number | null;
  status: 'live' | 'waiting' | 'unavailable' | 'off' | 'idle' | 'ended';
  replicas: Array<{ role: string; t_from: number; t_to: number; text: string }>;
}

/**
 * Лента текущего разговора.
 *
 * ⭐ Коротким запросом, а не потоком событий, хотя §13.3 называет SSE.
 * Замер сервера: php-fpm держит пять рабочих процессов на всю админку, а
 * открытый поток занимает один целиком, пока живёт. Три открытых кабинета —
 * и админка встаёт для всех. Опрос раз в две секунды даёт ту же задержку и
 * держит процесс миллисекунды.
 */
export async function fetchLiveTranscript(): Promise<LiveTranscriptPayload> {
  const { data } = await apiClient.get<LiveTranscriptPayload>('/telephony/workplace/transcript');

  return data;
}

/** `s726`: отменить идущий мне набор — клиентке перестают идти гудки. */
export async function otmenitNabor(): Promise<void> {
  await apiClient.post('/telephony/workplace/nabor/otmenit');
}

/** `s726`: «Позвонить» из карточки — звонок начинает станция. */
export async function pozvonitIzKartochki(obyekt: { inquiry_id?: number; booking_id?: number; line_id?: number }): Promise<void> {
  otmetitSvoyNabor();
  await apiClient.post('/doska/pozvonit', obyekt);
}

/** `s726`: ручной набор номера с ВЫБРАННОЙ линии — звонок начинает станция. */
export async function nabratNomerSLinii(phone: string, lineId: number): Promise<void> {
  otmetitSvoyNabor();
  await apiClient.post('/telephony/workplace/nabor/nomer', { phone, line_id: lineId });
}

/** `s730`: перезвонить по пропущенному входящему — номер знает сервер, звонок начинает станция. */
export async function perezvonitPropushchennomu(taskId: number): Promise<void> {
  otmetitSvoyNabor();
  await apiClient.post(`/telephony/workplace/propushchennye/${taskId}/perezvonit`);
}

/** Линия, с которой можно звонить вручную. */
export interface LiniyaNabora {
  id: number;
  name: string;
  number: string | null;
}

const KLYUCH_LINII = 'opsmind.tel.liniya';

/** Выбранная в пульте линия — помнится в этом браузере; хранилище может быть закрыто. */
export function vybrannayaLiniya(): number | null {
  try {
    const v = Number(window.localStorage.getItem(KLYUCH_LINII));
    return Number.isInteger(v) && v > 0 ? v : null;
  } catch {
    return null;
  }
}

export function zapomnitLiniyu(id: number): void {
  try {
    window.localStorage.setItem(KLYUCH_LINII, String(id));
  } catch {
    // хранилище закрыто — выбор живёт до перезагрузки
  }
}
